/**
 * @module AdapterCard
 * @package @geenius/adapters/solidjs-solidui
 * @description Renders the SolidUI adapter summary card for the SolidJS
 * SolidUI variant. The card surfaces provider status, latency, and domain
 * metadata for one adapter domain in dashboard views.
 */

import type { AdapterDomain, AdapterStatusInfo } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
} from "@geenius/adapters";
import type { JSX } from "solid-js";
import { Show } from "solid-js";
import { cx } from "../lib/cx";
import { AdapterStatusBadge } from "./AdapterStatusBadge";

/**
 * Props for rendering a single adapter summary card in the SolidUI dashboard grid.
 *
 * @property domain Adapter domain represented by the card.
 * @property status Current provider health and metadata for the domain.
 * @property onClick Optional callback fired when the card is activated.
 * @property class Optional class name merged into the card container.
 */
export interface AdapterCardProps {
  domain: AdapterDomain;
  status: AdapterStatusInfo;
  onClick?: () => void;
  class?: string;
}

/**
 * Renders a clickable SolidUI summary card for one adapter domain.
 *
 * @param props Card props including the adapter domain, status payload, and click handler.
 * @returns An interactive button when selection is enabled, otherwise a static summary card.
 */
export function AdapterCard(props: AdapterCardProps): JSX.Element {
  const content = (
    <>
      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-2xl shadow-sm transition-transform duration-300 group-hover:scale-105">
        {DOMAIN_ICONS[props.domain]}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="truncate text-sm font-bold text-foreground">
            {DOMAIN_LABELS[props.domain]}
          </h3>
          <AdapterStatusBadge status={props.status.status} />
        </div>
        <p class="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
          {DOMAIN_DESCRIPTIONS[props.domain]}
        </p>
        <Show when={props.status.provider && props.status.provider !== "none"}>
          <div class="mt-2 flex items-center gap-2">
            <span class="rounded-md border border-border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold text-muted-foreground">
              {props.status.provider}
            </span>
            <Show when={props.status.latency}>
              <span class="text-[0.625rem] text-muted-foreground">
                {props.status.latency}ms
              </span>
            </Show>
          </div>
        </Show>
        <Show when={props.status.error}>
          <p class="mt-1.5 truncate text-[0.625rem] text-destructive">
            {props.status.error}
          </p>
        </Show>
      </div>
    </>
  );

  const className = cx(
    "solidui-adapter-card group relative flex w-full items-start gap-4 overflow-hidden rounded-md border border-border bg-card p-5 text-left text-card-foreground shadow-sm transition-colors duration-200",
    props.onClick
      ? "hover:border-primary/20 hover:bg-accent/5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      : "cursor-default",
    props.class,
  );

  if (!props.onClick) {
    return (
      <article
        class={className}
        aria-label={`${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`}
      >
        {content}
      </article>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      class={className}
      aria-label={`${DOMAIN_LABELS[props.domain]} adapter - ${props.status.status}`}
    >
      {content}
    </button>
  );
}
